import { MessageGroup, MessageGroups } from '../state';
import { Message, MessageCategory } from '../types';
import { Loadable, LoadableState } from './useLoadableState';

export type UpdateMessagesResult = {
  updatedMessages: Message[];
  groups: LoadableState<MessageGroups>;
};

/**
 * Updates the notifications matching the given predicate using the update function.
 * @param messages The messages
 * @param predicate The predicate (similar to callback in Array#filter or Array#find)
 * @param update The update function. Should return a new object if the Message was updated, and the same object if it was not.
 * @returns An updated message list and the grouped messages object.
 */
export function updateNotificationMessages(
  messages: LoadableState<MessageGroups>,
  predicate: (message: Message) => boolean,
  update: (message: Message) => Message,
): UpdateMessagesResult {
  return Loadable.getOrElse(
    Loadable.map(messages, msgs => {
      const categoryToMessagesTuples = Object.entries(msgs.groups) as [MessageCategory, Message[]][];
      const freshlyReadMessages: Map<number, Message> = new Map();

      for (const [_, msgs] of categoryToMessagesTuples) {
        for (const msg of msgs) {
          if (predicate(msg)) {
            const updatedMsg = update(msg);
            if (updatedMsg !== msg) {
              freshlyReadMessages.set(msg.id, updatedMsg);
            }
          }
        }
      }

      const groups = categoryToMessagesTuples.reduce(
        (output, [key, msgs]): Record<MessageCategory, MessageGroup> => ({
          ...output,
          [key]: msgs.map(msg => freshlyReadMessages.get(msg.id) ?? msg),
        }),
        {} as Record<MessageCategory, MessageGroup>,
      );

      return {
        updatedMessages: Array.from(freshlyReadMessages.values()),
        groups: Loadable.loaded({ groups }),
      };
    }),
    { groups: messages, updatedMessages: [] },
  );
}

export function getUnreadMessageCount(messages: LoadableState<MessageGroups>): number {
  return Loadable.getOrElse(
    Loadable.map(messages, ({ groups }) => {
      const unreadCount = Object.values(groups)
        .flat()
        .filter(msg => !msg.isRead).length;
      return unreadCount;
    }),
    0,
  );
}
